import type { Page404Options } from '@Types/404/page404';
import type { BtnStyleElement } from '@Types/Shared/Button';
import type { SectionDecorationOptions } from '@Types/Shared/Common';
import type { Options } from '@Types/Shared/Options';
import type { MenusType, SiteType } from '@Types/Shared/SiteDatas';

import { getHomeBtnSettingsToCSS } from '@Blocks/_lib/btns';

import { __Front } from '@/src/Views/Shared/lib';
import { Button } from '@Views/Shared/Button/Button';
import { getSingleBtnStyle } from '@Views/Shared/GlobalStyles/GlobalStyles';
import { Bubble } from '@Views/Shared/Icons';
import type { Translation } from '@Views/Shared/Translations';

import Layout from '../../Shared/Layout/Layout';

type notFoundType = {
	site: SiteType;
	menus: MenusType;
	minos: Options;
	translations: Translation[];
};

export default function notFound(props: notFoundType) {
	const { site, menus, minos, translations } = props;

	const page404: Page404Options = { ...minos.layout.page404 };
	const sectionDecoration: SectionDecorationOptions = { ...minos.layout.sectionDecoration };
	const btnStyle = getSingleBtnStyle(minos, page404.btnStyle);

	const siteURL = site.url;

	const Props: NotFoundViewProps = {
		sectionDecoration,
		siteURL,
		page404,
		btnStyle,
	};

	return (
		<Layout
			blocks={['404']}
			seo={null}
			site={site}
			translations={translations}
			menus={menus}
			minos={minos}
		>
			<NotFoundView {...Props} />
		</Layout>
	);
}

export type NotFoundViewProps = {
	sectionDecoration: SectionDecorationOptions;
	siteURL: string;
	page404: Page404Options;
	isPreview?: boolean;
	btnStyle?: BtnStyleElement;
};

export const NotFoundView = (props: NotFoundViewProps) => {
	const { sectionDecoration, siteURL, page404, isPreview = false, btnStyle } = props;

	return (
		<article className='relative overflow-hidden'>
			{/* Bubble decoration */}
			{sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'bubble' && (
				<>
					<Bubble
						position='left'
						className='minos-decoration-bubble-left top-[-250px]'
						pathClassName='fill-minos-decoration-bubble-left'
					/>
				</>
			)}
			<div
				className={`minos-grid bg-minos-404-bg-color ${sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'wave' ? 'pb-16 pt-12 tablet:pb-24 tablet:pt-12 desktop:pb-40 desktop:pt-24' : 'py-5 tablet:py-12 desktop:py-24'}`}
			>
				<div
					className={`relative z-[11] col-start-2 col-end-14 py-10 tablet:py-24 desktop:py-72 ${page404.containerType === 'block' && 'rounded-minos-404-container border-minos-404-container-border-width border-minos-404-container-border-color bg-minos-404-bg-container-color px-8 tablet:px-10 desktop:px-16'}`}
					data-aos={isPreview ? '' : 'fade-up'}
				>
					<div
						className={`block max-w-72 tablet:max-w-none ${page404.textAlign === 'center' ? 'm-auto text-center' : page404.textAlign === 'left' ? 'mr-auto' : 'text-right'}`}
					>
						<h1 className='minos-404-title-font-style text-4xl tablet:minos-404-title-font-style'>
							{__Front('front.404_Error')}
						</h1>
						<p className='minos-404-description-font-style my-5 text-base tablet:minos-404-description-font-style tablet:my-8'>
							{__Front('front.404_subtitle')}
						</p>
						<Button
							label={__Front('front.back_to_homepage')}
							url={siteURL}
							style='primary'
							urlType='link'
							className='minos-404-btn tablet:minos-404-btn'
							btnClassName={getHomeBtnSettingsToCSS(btnStyle, '404')}
						/>
					</div>
				</div>
			</div>
			{/* Bubble decoration */}
			{sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'bubble' && (
				<>
					<Bubble
						position='right'
						className='minos-decoration-bubble-right'
						pathClassName='fill-minos-decoration-bubble-right'
					/>
				</>
			)}
		</article>
	);
};
